import { OBJ } from '../constants';

/**
 * Annotation object model.
 *
 * Every object lives in page view space (PDF points, origin top-left, y down)
 * and is described by an axis-aligned box `{ x, y, w, h }` plus a rotation in
 * degrees about the box centre. Freehand strokes additionally carry `points`
 * normalised to 0–1 inside that box, so resizing a stroke is just a box change.
 */

let counter = 0;
export function newId(prefix = 'o') {
    counter += 1;
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return `${prefix}_${crypto.randomUUID()}`;
    return `${prefix}_${Date.now().toString(36)}_${counter}`;
}

const base = (type, rect) => ({
    id: newId(type),
    type,
    x: rect.x,
    y: rect.y,
    w: Math.max(1, rect.w),
    h: Math.max(1, rect.h),
    rotation: 0,
    opacity: 1,
});

export const makeText = (rect, settings, text = '') => ({
    ...base(OBJ.TEXT, rect),
    text,
    fontFamily: settings.fontFamily,
    // Set by "Edit text" only: the document's own face, with the catalogue
    // family behind it. `null` means "use the catalogue family".
    fontStack: settings.fontStack || null,
    fontLabel: settings.fontLabel || null,
    // pdf.js id of the document's own font program, so the export can embed it.
    fontId: settings.fontId || null,
    fontSize: settings.fontSize,
    color: settings.color,
    bold: settings.bold,
    italic: settings.italic,
    underline: settings.underline,
    align: settings.align,
    lineHeight: settings.lineHeight,
    background: settings.background || 'transparent',
    autoHeight: true,
});

export const makeWhiteout = (rect, fill) => ({ ...base(OBJ.WHITEOUT, rect), fill });

export const makeShape = (kind, rect, settings) => ({
    ...base(kind, rect),
    stroke: settings.stroke,
    strokeWidth: settings.strokeWidth,
    fill: settings.fill,
    // Lines and arrows keep their direction: the drag may run right-to-left or
    // bottom-to-top, and a bare box would silently normalise that away.
    ...(kind === OBJ.LINE || kind === OBJ.ARROW
        ? { flipX: !!settings.flipX, flipY: !!settings.flipY }
        : {}),
});

export const makeInk = (points, settings, mode = 'pen') => {
    const box = pointsBox(points, mode === 'highlight' ? settings.strokeWidth : settings.strokeWidth);
    return {
        ...base(OBJ.INK, box),
        mode,
        color: settings.color,
        strokeWidth: settings.strokeWidth,
        opacity: mode === 'highlight' ? 0.45 : 1,
        points: normalisePoints(points, box),
    };
};

export const makeImage = (rect, src, naturalW, naturalH, kind = OBJ.IMAGE) => ({
    ...base(OBJ.IMAGE, rect),
    src,
    naturalW,
    naturalH,
    kind,
    // Pictures and signatures are the one thing users never want stretched, so
    // the ratio is locked until they say otherwise.
    lockAspect: true,
});


/**
 * A picture of the source page, swapped for another one.
 *
 * `replacesImage` is the rect the old picture was painted at, in the SOURCE
 * page's view space — the exporter matches it back to a `Do` in the content
 * stream, so it must not move when the paper changes. The object's own box is
 * the same rect on the page as it is now, which is what makes it selectable and
 * deletable; deleting it puts the original picture back.
 */
export const makeImageSwap = (rect, src, naturalW, naturalH, replacesImage) => ({
    ...base(OBJ.IMAGE_SWAP, rect),
    src,
    naturalW,
    naturalH,
    replacesImage,
});

/** Undefined counts as locked, so objects saved before the option existed keep their shape. */
export const isAspectLocked = (obj) => obj.lockAspect !== false;

export const makeStamp = (type, rect, settings) => ({
    ...base(type, rect),
    color: settings.color,
    strokeWidth: settings.strokeWidth,
});

export const makeNote = (rect, settings, text = '') => ({
    ...base(OBJ.NOTE, rect),
    text,
    color: settings.color,
});

export const makeLink = (rect, settings, url = '', targetPage = null) => ({
    ...base(OBJ.LINK, rect),
    url,
    targetPage,
    color: settings.color,
});

/** Bounding box of a raw point list, padded by half the stroke width. */
export function pointsBox(points, strokeWidth = 0) {
    const pad = Math.max(1, strokeWidth / 2);
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    for (const [x, y] of points) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    }
    if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 1, h: 1 };
    return {
        x: minX - pad,
        y: minY - pad,
        w: Math.max(1, maxX - minX + pad * 2),
        h: Math.max(1, maxY - minY + pad * 2),
    };
}

const normalisePoints = (points, box) => points.map(([x, y]) => [
    (x - box.x) / box.w,
    (y - box.y) / box.h,
]);

/** Absolute (view-space) points of a freehand object. */
export const absolutePoints = (obj) => obj.points.map(([nx, ny]) => [
    obj.x + nx * obj.w,
    obj.y + ny * obj.h,
]);

export function rectFromDrag(a, b, { square = false } = {}) {
    let w = b.x - a.x;
    let h = b.y - a.y;
    if (square) {
        const size = Math.max(Math.abs(w), Math.abs(h));
        w = Math.sign(w || 1) * size;
        h = Math.sign(h || 1) * size;
    }
    return {
        x: Math.min(a.x, a.x + w),
        y: Math.min(a.y, a.y + h),
        w: Math.abs(w),
        h: Math.abs(h),
        flipX: w < 0,
        flipY: h < 0,
    };
}

/** Point-in-object test in view space, honouring rotation. */
export function hitTest(obj, point) {
    const cx = obj.x + obj.w / 2;
    const cy = obj.y + obj.h / 2;
    const angle = (-(obj.rotation || 0) * Math.PI) / 180;
    const dx = point.x - cx;
    const dy = point.y - cy;
    const lx = dx * Math.cos(angle) - dy * Math.sin(angle) + cx;
    const ly = dx * Math.sin(angle) + dy * Math.cos(angle) + cy;
    const slack = 3;
    return lx >= obj.x - slack && lx <= obj.x + obj.w + slack
        && ly >= obj.y - slack && ly <= obj.y + obj.h + slack;
}

/** Topmost object under a point (objects are painted in array order). */
export function pickObject(objects, point) {
    for (let i = objects.length - 1; i >= 0; i -= 1) {
        if (hitTest(objects[i], point)) return objects[i];
    }
    return null;
}

export function cloneObject(obj, dx = 12, dy = 12) {
    return { ...obj, id: newId(obj.type), x: obj.x + dx, y: obj.y + dy };
}
